import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { SearchBar } from '../components/SearchBar';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { RefreshCw, Package, Plus, MoreVertical, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
// Mock product data
const mockProducts = [{
  id: '1',
  name: '2022 Reserve Shiraz',
  description: 'Full-bodied red wine with rich berry flavors',
  imageUrl: '',
  rrp: 48.0,
  salePrice: 45.0,
  stock: 156,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '15/01/2024',
  tags: ['Red Wine', 'Premium']
}, {
  id: '2',
  name: '2021 Chardonnay Reserve',
  description: 'Elegant white wine with citrus notes',
  imageUrl: '',
  rrp: 42.0,
  salePrice: 38.0,
  stock: 8,
  stockStatus: 'Low Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '12/01/2024',
  tags: ['White Wine', 'Premium']
}, {
  id: '3',
  name: 'Pinot Noir Estate 2020',
  description: 'Smooth and refined with cherry undertones',
  imageUrl: '',
  rrp: 55.0,
  salePrice: 52.0,
  stock: 0,
  stockStatus: 'Out of Stock' as const,
  syncStatus: 'SYNC_ERROR' as const,
  createdAt: '08/01/2024',
  tags: ['Red Wine']
}, {
  id: '4',
  name: 'Sauvignon Blanc 2023',
  description: 'Crisp and refreshing with tropical fruit',
  imageUrl: '',
  rrp: 32.0,
  salePrice: 28.0,
  stock: 243,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '20/12/2023',
  tags: ['White Wine']
}, {
  id: '5',
  name: 'Cabernet Sauvignon 2019',
  description: 'Bold and complex with dark fruit flavors',
  imageUrl: '',
  rrp: 58.0,
  salePrice: 55.0,
  stock: 89,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '15/12/2023',
  tags: ['Red Wine', 'Premium']
}, {
  id: '6',
  name: 'Rosé Grenache 2023',
  description: 'Light and fruity summer wine',
  imageUrl: '',
  rrp: 28.0,
  salePrice: 25.0,
  stock: 6,
  stockStatus: 'Low Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '10/12/2023',
  tags: ['Rosé']
}, {
  id: '7',
  name: 'Merlot Reserve 2020',
  description: 'Smooth and velvety with plum notes',
  imageUrl: '',
  rrp: 46.0,
  salePrice: 42.0,
  stock: 124,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '05/12/2023',
  tags: ['Red Wine']
}, {
  id: '8',
  name: 'Riesling 2023',
  description: 'Sweet and aromatic with floral notes',
  imageUrl: '',
  rrp: 35.0,
  salePrice: 32.0,
  stock: 0,
  stockStatus: 'Out of Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '01/12/2023',
  tags: ['White Wine']
}, {
  id: '9',
  name: 'Tempranillo 2021',
  description: 'Spanish-style red with spicy finish',
  imageUrl: '',
  rrp: 44.0,
  salePrice: 40.0,
  stock: 67,
  stockStatus: 'In Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '28/11/2023',
  tags: ['Red Wine']
}, {
  id: '10',
  name: 'Viognier 2023',
  description: 'Aromatic white with stone fruit flavors',
  imageUrl: '',
  rrp: 38.0,
  salePrice: 35.0,
  stock: 4,
  stockStatus: 'Low Stock' as const,
  syncStatus: 'IN_SYNC' as const,
  createdAt: '25/11/2023',
  tags: ['White Wine', 'Premium']
}];
// Top selling products data
const topSellingProducts = [{
  name: '2022 Reserve Shiraz',
  sold: 342
}, {
  name: '2021 Chardonnay Reserve',
  sold: 287
}, {
  name: 'Sauvignon Blanc 2023',
  sold: 256
}];
export function ProductsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage_, setCurrentPage_] = useState(1);
  const pageSize = 10;
  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    stockStatus: [],
    tags: [],
    created: []
  });
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [customDateRanges, setCustomDateRanges] = useState<Record<string, {
    startDate: string;
    endDate: string;
  }>>({});
  // State for managing which product's dropdown is open
  const [openProductDropdown, setOpenProductDropdown] = useState<string | null>(null);
  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  // Calculate stats
  const stats = useMemo(() => {
    const lowStock = mockProducts.filter(p => p.stockStatus === 'Low Stock').length;
    const outOfStock = mockProducts.filter(p => p.stockStatus === 'Out of Stock').length;
    const totalProducts = mockProducts.length;
    return {
      lowStock,
      outOfStock,
      totalProducts
    };
  }, []);
  // Available filters
  const availableFilters: Filter[] = [{
    id: 'stockStatus',
    label: 'Stock Status',
    options: [{
      id: '1',
      label: 'In Stock',
      value: 'in-stock'
    }, {
      id: '2',
      label: 'Out of Stock',
      value: 'out-of-stock'
    }, {
      id: '3',
      label: 'Low Stock',
      value: 'low-stock'
    }],
    selectedValues: filterValues.stockStatus || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('stockStatus')
  }, {
    id: 'tags',
    label: 'Tags',
    options: [{
      id: '1',
      label: 'Red Wine',
      value: 'red-wine'
    }, {
      id: '2',
      label: 'White Wine',
      value: 'white-wine'
    }, {
      id: '3',
      label: 'Rosé',
      value: 'rose'
    }, {
      id: '4',
      label: 'Premium',
      value: 'premium'
    }],
    selectedValues: filterValues.tags || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('tags')
  }, {
    id: 'created',
    label: 'Created',
    options: [{
      id: '1',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '2',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '3',
      label: 'Last 90 days',
      value: 'last-90-days'
    }, {
      id: '4',
      label: 'Custom',
      value: 'custom'
    }],
    selectedValues: filterValues.created || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('created'),
    customDateRange: customDateRanges['created']
  }];
  // Filter products based on search and filters
  const filteredProducts = useMemo(() => {
    let products = mockProducts;
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      products = products.filter(product => product.name.toLowerCase().includes(query) || product.description.toLowerCase().includes(query));
    }
    // Stock status filter
    if (filterValues.stockStatus && filterValues.stockStatus.length > 0) {
      products = products.filter(product => {
        return filterValues.stockStatus.some(value => {
          if (value === 'in-stock') return product.stockStatus === 'In Stock';
          if (value === 'out-of-stock') return product.stockStatus === 'Out of Stock';
          if (value === 'low-stock') return product.stockStatus === 'Low Stock';
          return false;
        });
      });
    }
    // Tags filter
    if (filterValues.tags && filterValues.tags.length > 0) {
      products = products.filter(product => {
        return filterValues.tags.some(value => {
          const tagMap: Record<string, string> = {
            'red-wine': 'Red Wine',
            'white-wine': 'White Wine',
            rose: 'Rosé',
            premium: 'Premium'
          };
          return product.tags?.includes(tagMap[value]);
        });
      });
    }
    // Created date filter
    if (filterValues.created && filterValues.created.length > 0) {
      products = products.filter(product => {
        const createdDate = new Date(product.createdAt.split('/').reverse().join('-'));
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        return filterValues.created.some(value => {
          if (value === 'last-7-days') return daysDiff <= 7;
          if (value === 'last-30-days') return daysDiff <= 30;
          if (value === 'last-90-days') return daysDiff <= 90;
          if (value === 'custom') {
            const dateRange = customDateRanges['created'];
            if (dateRange && dateRange.startDate && dateRange.endDate) {
              const start = new Date(dateRange.startDate);
              const end = new Date(dateRange.endDate);
              end.setHours(23, 59, 59, 999);
              return createdDate >= start && createdDate <= end;
            }
            return true;
          }
          return false;
        });
      });
    }
    // Apply sorting
    if (sortConfig) {
      products = [...products].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'product':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'rrp':
            aValue = a.rrp;
            bValue = b.rrp;
            break;
          case 'salePrice':
            aValue = a.salePrice;
            bValue = b.salePrice;
            break;
          case 'stock':
            aValue = a.stock;
            bValue = b.stock;
            break;
          case 'stockStatus':
            const statusOrder: Record<string, number> = {
              'Out of Stock': 0,
              'Low Stock': 1,
              'In Stock': 2
            };
            aValue = statusOrder[a.stockStatus] ?? 0;
            bValue = statusOrder[b.stockStatus] ?? 0;
            break;
          case 'created':
            aValue = new Date(a.createdAt.split('/').reverse().join('-')).getTime();
            bValue = new Date(b.createdAt.split('/').reverse().join('-')).getTime();
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return products;
  }, [searchQuery, filterValues, customDateRanges, sortConfig]);
  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / pageSize);
  const paginatedProducts = filteredProducts.slice((currentPage_ - 1) * pageSize, currentPage_ * pageSize);
  // Check if any filters are active
  const hasActiveFilters = addedFilters.size > 0 || searchQuery.trim() !== '';
  // Get stock status badge variant
  const getStockStatusVariant = (status: string): 'success' | 'warning' | 'error' => {
    if (status === 'In Stock') return 'success';
    if (status === 'Low Stock') return 'warning';
    return 'error';
  };
  // Get sync status badge variant
  const getSyncStatusVariant = (status: string): 'success' | 'error' => {
    return status === 'IN_SYNC' ? 'success' : 'error';
  };
  // Handle filter change
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    const newFilterValues = {
      ...filterValues,
      [filterId]: Array.isArray(value) ? value : [value]
    };
    setFilterValues(newFilterValues);
  };
  // Handle filter remove
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    // Clear filter values
    const newFilterValues = {
      ...filterValues
    };
    newFilterValues[filterId] = [];
    setFilterValues(newFilterValues);
  };
  // Handle add filter
  const handleAddFilter = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.add(filterId);
    setAddedFilters(newAddedFilters);
  };
  // Handle toggle filter dropdown
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpenFilterDropdowns = new Set(openFilterDropdowns);
    newOpenFilterDropdowns.add(filterId);
    setOpenFilterDropdowns(newOpenFilterDropdowns);
  };
  // Handle custom date change
  const handleCustomDateChange = (filterId: string, startDate: string, endDate: string) => {
    setCustomDateRanges(prev => ({
      ...prev,
      [filterId]: {
        startDate,
        endDate
      }
    }));
  };
  // Handle clear all filters
  const handleClearAll = () => {
    setFilterValues({
      stockStatus: [],
      tags: [],
      created: []
    });
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  // Handle sort
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  // Handle low stock card click
  const handleLowStockClick = () => {
    setFilterValues({
      stockStatus: ['low-stock'],
      tags: [],
      created: []
    });
    setAddedFilters(new Set(['stockStatus']));
  };
  // Handle out of stock card click
  const handleOutOfStockClick = () => {
    setFilterValues({
      stockStatus: ['out-of-stock'],
      tags: [],
      created: []
    });
    setAddedFilters(new Set(['stockStatus']));
  };
  // Handle view product in Shopify
  const handleViewProductInShopify = (productId: string) => {
    // In a real app, this would open the Shopify admin URL
    console.log(`View product ${productId} in Shopify`);
    window.open(`https://store.myshopify.com/admin/products/${productId}`, '_blank');
    setOpenProductDropdown(null);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-4 md:p-6" data-id="element-8661">
      {/* NavBar */}
      <div className="mb-4 md:mb-6" data-id="element-8662">
        <NavBar tabs={[]} title="Products" data-id="element-8663" />
      </div>

      {/* Header Section */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-id="element-8664">
        <p className="text-[13px] md:text-[14px] text-[#64748B]" data-id="element-8665">
          Products synced from your Shopify store
        </p>
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8" data-id="element-8666">
        {/* Top Selling Card */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] p-4 md:p-5 shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-8667">
          <div className="text-[13px] md:text-[14px] text-[#64748B] font-medium mb-3" data-id="element-8668">
            Top Selling
          </div>
          <div className="space-y-2" data-id="element-8669">
            {topSellingProducts.map((product, index) => <div key={index} className="flex items-center justify-between" data-id="element-8670">
                <span className="text-[13px] md:text-[14px] text-[#0F172A]" data-id="element-8671">
                  {product.name}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-xl text-[11px] md:text-[12px] font-medium bg-[#DCFCE7] text-[#166534]" data-id="element-8672">
                  {product.sold} sold
                </span>
              </div>)}
          </div>
        </div>

        {/* Low Stock Card */}
        <button onClick={handleLowStockClick} className="bg-white rounded-lg border border-[#E2E8F0] p-4 md:p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)] hover:shadow-[0_4px_6px_rgba(15,23,42,0.1)] transition-shadow cursor-pointer text-left w-full" data-id="element-8673">
          <div className="text-[13px] md:text-[14px] text-[#64748B] font-medium mb-3" data-id="element-8674">
            Low Stock
          </div>
          <div className="text-[28px] md:text-[32px] font-semibold text-[#F59E0B]" data-id="element-8675">
            {stats.lowStock}
          </div>
        </button>

        {/* Out of Stock Card */}
        <button onClick={handleOutOfStockClick} className="bg-white rounded-lg border border-[#E2E8F0] p-4 md:p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)] hover:shadow-[0_4px_6px_rgba(15,23,42,0.1)] transition-shadow cursor-pointer text-left w-full" data-id="element-8676">
          <div className="text-[13px] md:text-[14px] text-[#64748B] font-medium mb-3" data-id="element-8677">
            Out of Stock
          </div>
          <div className="text-[28px] md:text-[32px] font-semibold text-[#EF4444]" data-id="element-8678">
            {stats.outOfStock}
          </div>
        </button>

        {/* Total Products Card */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] p-4 md:p-5 shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-8679">
          <div className="text-[13px] md:text-[14px] text-[#64748B] font-medium mb-3" data-id="element-8680">
            Total Products
          </div>
          <div className="text-[28px] md:text-[32px] font-semibold text-[#0F172A]" data-id="element-8681">
            {stats.totalProducts}
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-4 md:mb-6 p-4 md:p-6 shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-8682">
        <div className="flex flex-col gap-4" data-id="element-8683">
          {/* Search + Add Filter Button Row */}
          <div className="flex items-center gap-3" data-id="element-8684">
            <div className="flex-1" data-id="element-8685">
              <SearchBar placeholder="Search products..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-8686" />
            </div>
            <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-8687" />
          </div>

          {/* Active Filter Chips Row */}
          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-8688">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-8689" />
              <button onClick={handleClearAll} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-8690">
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Products Table */}
      {filteredProducts.length > 0 ? <>
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden mb-4 md:mb-6 shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-8691">
            <div className="overflow-x-auto" data-id="element-8692">
              <table className="w-full" data-id="element-8693">
                <thead className="bg-[#F1F5F9]" data-id="element-8694">
                  <tr data-id="element-8695">
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-8696">
                      <button onClick={() => handleSort('product')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-8697">
                        Product
                        {sortConfig?.key === 'product' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8698" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8699" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-8700" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[100px]" data-id="element-8701">
                      <button onClick={() => handleSort('rrp')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-8702">
                        RRP
                        {sortConfig?.key === 'rrp' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8703" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8704" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-8705" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[100px]" data-id="element-8706">
                      <button onClick={() => handleSort('salePrice')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-8707">
                        Sale Price
                        {sortConfig?.key === 'salePrice' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8708" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8709" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-8710" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[80px]" data-id="element-8711">
                      <button onClick={() => handleSort('stock')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors justify-end" data-id="element-8712">
                        Stock
                        {sortConfig?.key === 'stock' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8713" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8714" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-8715" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[120px]" data-id="element-8716">
                      <button onClick={() => handleSort('stockStatus')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-8717">
                        Stock Status
                        {sortConfig?.key === 'stockStatus' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8718" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8719" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-8720" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[150px]" data-id="element-8721">
                      Tags
                    </th>
                    <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[120px]" data-id="element-8722">
                      <button onClick={() => handleSort('created')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors justify-end" data-id="element-8723">
                        Created
                        {sortConfig?.key === 'created' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8724" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8725" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-8726" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider w-[80px]" data-id="element-8727">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-8728">
                  {paginatedProducts.map(product => <tr key={product.id} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-8729">
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-8730">
                        <div className="flex items-center gap-3" data-id="element-8731">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-center flex-shrink-0" data-id="element-8732">
                            <Package className="w-5 h-5 md:w-6 md:h-6 text-#94A3B8" data-id="element-8733" />
                          </div>
                          <div className="min-w-0" data-id="element-8734">
                            <div className="text-[14px] md:text-[16px] font-medium text-[#0F172A] truncate" data-id="element-8735">
                              {product.name}
                            </div>
                            <div className="text-[13px] md:text-[14px] text-[#64748B] truncate" data-id="element-8736">
                              {product.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-8737">
                        <span className="text-[14px] md:text-[16px] text-[#0F172A]" data-id="element-8738">
                          ${product.rrp.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-8739">
                        <span className="text-[14px] md:text-[16px] text-[#0F172A]" data-id="element-8740">
                          ${product.salePrice.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4 text-right" data-id="element-8741">
                        <span className="text-[14px] md:text-[16px] text-[#0F172A]" data-id="element-8742">
                          {product.stock}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-8743">
                        <Badge variant={getStockStatusVariant(product.stockStatus)} data-id="element-8744">
                          {product.stockStatus}
                        </Badge>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-8745">
                        <div className="flex flex-wrap gap-1" data-id="element-8746">
                          {product.tags?.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-[#E0F2FE] text-[#0284C7]" data-id="element-8747">
                              {tag}
                            </span>)}
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4 text-right" data-id="element-8748">
                        <span className="text-[13px] md:text-[14px] text-[#64748B]" data-id="element-8749">
                          {product.createdAt}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-8750">
                        <div className="flex justify-end" data-id="element-8751">
                          <div className="relative" data-id="element-8752">
                            <button onClick={() => setOpenProductDropdown(openProductDropdown === product.id ? null : product.id)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-8753">
                              <MoreVertical className="w-5 h-5 text-[#64748B]" data-id="element-8754" />
                            </button>

                            {openProductDropdown === product.id && <>
                                <div className="fixed inset-0 z-10" onClick={() => setOpenProductDropdown(null)} data-id="element-8755" />
                                <div className="absolute right-0 mt-2 w-56 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 py-1" data-id="element-8756">
                                  <button onClick={() => handleViewProductInShopify(product.id)} className="w-full px-4 py-2 text-left text-sm hover:bg-[#F0F9FF] transition-colors text-[#0F172A] flex items-center gap-2" data-id="element-8757">
                                    <ExternalLink className="w-4 h-4" data-id="element-8758" />
                                    View Product in Shopify
                                  </button>
                                </div>
                              </>}
                          </div>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-8759">
            <div className="text-[13px] text-[#64748B]" data-id="element-8760">
              Showing {(currentPage_ - 1) * pageSize + 1}-
              {Math.min(currentPage_ * pageSize, filteredProducts.length)} of{' '}
              {filteredProducts.length} products
            </div>
            <div className="flex items-center gap-2" data-id="element-8761">
              <button onClick={() => setCurrentPage_(Math.max(1, currentPage_ - 1))} disabled={currentPage_ === 1} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-8762">
                Previous
              </button>
              <div className="flex items-center gap-1" data-id="element-8763">
                {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage_(page)} className={`w-10 h-10 rounded-md text-[13px] font-medium transition-colors duration-200 ease-in-out ${page === currentPage_ ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-8764">
                    {page}
                  </button>)}
              </div>
              <button onClick={() => setCurrentPage_(Math.min(totalPages, currentPage_ + 1))} disabled={currentPage_ === totalPages} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-8765">
                Next
              </button>
            </div>
          </div>
        </> : <div className="bg-white rounded-lg border border-[#E2E8F0] p-8 md:p-12 text-center shadow-0_1px_3px_rgba(15,23,42,0.08)" data-id="element-8766">
          <Package className="w-10 h-10 md:w-12 md:h-12 text-#94A3B8 mx-auto mb-4" data-id="element-8767" />
          <h3 className="text-[18px] md:text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-8768">
            No products found
          </h3>
          <p className="text-[14px] md:text-[16px] text-[#64748B] mb-6" data-id="element-8769">
            {hasActiveFilters ? 'Try adjusting your filters or search query' : 'No products are available at this time'}
          </p>
          {hasActiveFilters && <Button variant="outline" onClick={handleClearAll} data-id="element-8770">
              Clear all filters
            </Button>}
        </div>}
    </div>;
}